import React, {useState, useContext, useEffect, useRef} from 'react'
import AppLayout from 'components/AppLayout/AppLayout';
import AppDrawer from 'components/AppDrawer/AppDrawer';
import Typography from '@mui/material/Typography';
import { DataContext } from 'contexts/DataContext';
import { Gantt, Task, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import EditableGrid from 'components/EditableGrid/EditableGrid';
import 'i18n/config';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import moment from 'moment';

import { get, update, destroy, getColumns, getIncrements, getWhatIf, WhatIfResult } from './api'
import { getStatus } from 'pages/Assessment/api';
import './treatment.css';

import { TaskListTableCustom } from './task-list-table';
import { ViewSwitcher } from './view-switcher';
import { WhatIfPanel } from './WhatIfPanel';
import Detail from 'pages/Treatments/Detail';


const Plan = () => {
    const { t } = useTranslation();
    const dataContext = useContext(DataContext);
    const [tasks, setTasks] = useState([] as Task[]);
    const [whatIf, setWhatIf] = useState<WhatIfResult | null>(null);
    const [whatIfLoading, setWhatIfLoading] = useState(false);
    const [selectedRange, setSelectedRange] = useState<{ start: Date; end: Date } | null>(null);
    const addButtonRef = useRef(null);
    const [status, setStatus] = useState({} as any);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [increment, setIncrement] = useState({} as any);

    const [groupFilter, setGroupFilter] = useState('');

    const [columns, setColumns] = useState([] as GridColDef[]);
    const [view, setView] = useState<ViewMode>(ViewMode.QuarterYear);
    let columnWidth = 65;
    if (view === ViewMode.Year) {
      columnWidth = 350;
    } else if (view === ViewMode.Month) {
      columnWidth = 300;
    } else if (view === ViewMode.Week) {
      columnWidth = 250;
    }

    function getStartEndDateForProject(tasks: Task[], projectId: string) {
      let projectTasks = [];
      if(projectId!="") {
        projectTasks = tasks.filter(t => t.project === projectId);
      } else {
        projectTasks = tasks;
      }
      let start = projectTasks[0].start instanceof Date ? projectTasks[0].start : new Date(projectTasks[0].start);
      let end = projectTasks[0].end instanceof Date ? projectTasks[0].end : new Date(projectTasks[0].end);
    
      for (let i = 0; i < projectTasks.length; i++) {
        const task = projectTasks[i];
        task.start = task.start instanceof Date ? task.start : new Date(task.start);
        task.end = task.end instanceof Date ? task.end : new Date(task.end);

        if (start.getTime() > task.start.getTime()) {
          start = task.start;
        }
        if (end.getTime() < task.end.getTime()) {
          end = task.end;
        }
      }
      return [start, end];
    }

    
    useEffect(() => {
      if(dataContext.loading) return;
      getColumns(dataContext.language, setGroupFilter).then((response) => {
        setColumns(response);
      });
    }, [dataContext.loading, dataContext.language]);

    useEffect(() => {
      if(dataContext.loading) return;
      if(!dataContext.currentOrganization) return;
      if(!dataContext.currentAssessment) return;
      dataContext.changeShowLoader(true);

      getStatus(dataContext.currentOrganization, dataContext.currentAssessment).then((response) => {
        setStatus(response);

        getIncrements(dataContext.currentAssessment).then((response) => {
          setIncrement(response);
          setDataLoaded(true)
        })
      });

      
      


      get(dataContext.currentAssessment).then((response) => {
        var items = [];
        response = response.filter((item:any) => item.type !== 'group');

        let groupedByCategory: { [key: string]: any[] } = response.reduce((acc:any, item:any) => {
          // If the array for the current category doesn't exist, create it
          if (!acc[item.category]) {
              acc[item.category] = [];
          }
          // Add the item to the array corresponding to its category
          acc[item.category].push(item);
          return acc;
      }, {} as { [key: string]: any[] });
      
      for (let category in groupedByCategory) {
        //console.log(`Category: ${category}`);
        
        let projectId = category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        let project = {} as any;
        project.type="project";
        project.id = projectId;
        project.name = category;
        project.progress = 0;
        const [start,end] = getStartEndDateForProject(groupedByCategory[category],'');

        project.start = start;
        project.end = end;
        project.hideChildren= true;

        items.push(project);

        groupedByCategory[category].forEach(item => {
        
          item.start = new Date(item.start);
          item.end = new Date(item.end);
          item.type = "task";
          item.project = item.category.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          item.styles  = {};
          item.styles.progressColor = item.implementation == 1 ? '#aa6408' : '#2b589b'
          item.styles.progressSelectedColor = item.implementation == 1 ? '#aa6408' : '#2b589b'
          item.styles.backgroundColor = item.implementation == 1 ? '#FE9D0C' : '#3367b9'
          item.styles.backgroundSelectedColor = item.implementation == 1 ? '#FE9D0C' : '#3367b9'
        
          //if(item.id=="138") {
          //  item.dependencies = [149,140]
          //}
          //if(item.id=="121") {
          //  item.dependencies = [138]
          //}
          if(item.id!=item.depend) {
            item.dependencies = [item.depend];
          } else {
            item.dependencies = [];
          }
          items.push(item);
      
        });
      }


        //console.log(items);
        setTasks(items);
      })
     
      dataContext.changeShowLoader(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataContext.loading, dataContext.currentOrganization, dataContext.currentAssessment, dataContext.reloadParent]);

    useEffect(()=>{

      //getRoles().then((response:any) => {
        let implementationCol = columns.filter(column => column.field === "implementation")[0] as any;
        if(implementationCol) {
          let implementation = [{id:1, name:t('One shot')},{id:2,name:t('Recurrent')}];
          implementationCol.valueOptions = implementation.map((role:any) => { return {value: role.id, label: role.name} });
        }

        let coverageCol = columns.filter(column => column.field === "coverage")[0] as any;
        if(coverageCol) {
          let coverage = [{id:1, name:t('Complete')},{id:2,name:t('Partial')}];
          coverageCol.valueOptions = coverage.map((role:any) => { return {value: role.id, label: role.name} });
        }

        let statusCol = columns.filter(column => column.field === "status")[0] as any;
        if(statusCol) {
          let status = [
            {id:1, name:t('Pending')},
            {id:2,name:t('Approved')},
            {id:3,name:t('Ongoing')},
            {id:4,name:t('Completed')},
            {id:5,name:t('Canceled')},
          ];

          statusCol.valueOptions = status.map((role:any) => { return {value: role.id, label: role.name} });
        }
      //});
    })

    const handleTaskChange = (task: Task) => {
      console.log("On date change Id:" + task.id);
      dataContext.changeShowLoader(true);
      update(task, dataContext.currentAssessment).then((response)=>{
        //dataContext.toggleReloadParent()
        dataContext.changeShowLoader(false);
      })

      let newTasks = tasks.map(t => (t.id === task.id ? task : t));
      if (task.project) {
        const [start, end] = getStartEndDateForProject(newTasks, task.project);
        const project = newTasks[newTasks.findIndex(t => t.id === task.project)];
        if (
          project.start.getTime() !== start.getTime() ||
          project.end.getTime() !== end.getTime()
        ) {
          const changedProject = { ...project, start, end };
          newTasks = newTasks.map(t =>
            t.id === task.project ? changedProject : t
          );
        }
      }
      setTasks(newTasks);
    };
  
    const handleTaskDelete = (task: Task) => {
      const conf = window.confirm("Are you sure about " + task.name + " ?");
      if (conf) {
        setTasks(tasks.filter(t => t.id !== task.id));
      }
      return conf;
    };
  
    const handleProgressChange = async (task: Task) => {
      setTasks(tasks.map(t => (t.id === task.id ? task : t)));
      console.log("On progress change Id:", task);
      dataContext.changeShowLoader(true);
      update(task, dataContext.currentAssessment).then((response)=>{
        dataContext.toggleReloadParent()
        dataContext.changeShowLoader(false);
      })
    };
  
    const handleDblClick = (task: Task) => {
      //alert("On Double Click event Id:" + task.id);
    };
  
    const handleClick = (task: Task) => {
      //console.log("On Click event Id:" + task.id);
      if(task.type=='project') {
        //handleExpanderClick(task);
        //task.hideChildren = !task.hideChildren;
      }
    };
  
    const handleSelect = (task: Task, isSelected: boolean) => {
      //console.log(task.name + " has " + (isSelected ? "selected" : "unselected"));
    };
  
    const handleExpanderClick = (task: Task) => {
      setTasks(tasks.map(t => (t.id === task.id ? task : t)));
      //console.log("On expander click Id:",task);
      setGroupFilter(task.name)
    };

    const handleSelectedRange = (range: { start: Date; end: Date }) => {
      if(!dataContext.currentAssessment) return;
      const format = (date: Date) => moment(date).format('YYYY-MM-DD');
      setSelectedRange(range);
      setWhatIfLoading(true);
      getWhatIf(dataContext.currentAssessment, format(range.start), format(range.end))
        .then(setWhatIf)
        .catch(() => setWhatIf(null))
        .finally(() => setWhatIfLoading(false));
    };

    const clearSelectedRange = () => {
      setSelectedRange(null);
      setWhatIf(null);
    };

    
    return (
      <>
      <AppLayout drawerOpen={true} drawer={<AppDrawer />} 
        title={<><Typography variant='pageTitle'>{'Plan'}</Typography></>}>
      
        {tasks.length>0 && <>
        <div className='dark' style={(!dataContext.darkMode ? {
          '--gantt-header-bg': '#f5f5f5',
          '--gantt-header-text': '#333',
          '--gantt-header-tick': '#c4c4c4',
          '--gantt-row-bg': '#ffffff',
          '--gantt-row-bg-alt': '#f0f0f0',
          '--gantt-selected-date-bg': '#e0e0e0',
          '--gantt-list-bg': '#ffffff',
          '--gantt-list-bg-alt': '#f3f3f3',
          '--gantt-list-text': '#000000',
        } : {
          '--gantt-list-bg': '#1e1e1e',
          '--gantt-list-bg-alt': '#282828',
          '--gantt-list-text': '#ffffff',
        }) as unknown as React.CSSProperties}>
          <WhatIfPanel result={whatIf} loading={whatIfLoading} onClose={clearSelectedRange} />
          <div style={{marginBottom:10}}>
          <ViewSwitcher
            viewMode={view}
            onViewModeChange={viewMode => setView(viewMode)}
            />
            </div>
        <Gantt
        columnWidth={columnWidth}
        tasks={tasks} 
        viewMode={view}
        locale='it'
        timeStep={24*60*60*1000}
        onDateChange={handleTaskChange}
        onDelete={handleTaskDelete}
        onProgressChange={handleProgressChange}
        onDoubleClick={handleDblClick}
        onClick={handleClick}
        onSelect={handleSelect}
        onExpanderClick={handleExpanderClick}
        selectedRange={selectedRange}
        onSelectedRange={handleSelectedRange}
        TooltipContent={()=><></>}
        TaskListHeader={(params)=>{
          return <>
            <div className="project-list-header" style={{height:params.headerHeight,minWidth: '100%', backgroundColor: 'var(--gantt-list-bg, #ffffff)'}}>
            <div className="_1nBOt">
              <div className="_WuQ0f" style={{minWidth: '100%', padding:10}}></div>
            </div>
          </div>
          </>
        }}
        TaskListTable={(params) => <TaskListTableCustom {...params} />}
        />   
        </div>
        <hr
          style={{borderColor:"#FD9D0D", marginTop:20}}
        />
        </>}
        <Button variant={groupFilter == '' ? 'contained' : 'outlined'} onClick={()=>{setGroupFilter('')}}>All</Button>
        <Button variant={groupFilter == 'Policy, Guidelines and Procedures' ? 'contained' : 'outlined'} onClick={()=>{setGroupFilter('Policy, Guidelines and Procedures')}}>Policy, Guidelines and Procedures</Button>
        <Button variant={groupFilter == 'Cyber Security Solutions' ? 'contained' : 'outlined'} onClick={()=>{setGroupFilter('Cyber Security Solutions')}}>Cyber Security Solutions</Button>
        <Button variant={groupFilter == 'Cyber Operational Activities' ? 'contained' : 'outlined'} onClick={()=>{setGroupFilter('Cyber Operational Activities')}}>Cyber Operational Activities</Button>
        <Button variant={groupFilter == 'Cyber Security Services' ? 'contained' : 'outlined'} onClick={()=>{setGroupFilter('Cyber Security Services')}}>Cyber Security Services</Button>
        {dataContext.currentAssessment && 
        <EditableGrid addButtonRef={addButtonRef} hideAddButton={true} columns={columns} 
        details={(props) => <Detail {...props} tasks={tasks} />}
        hideDeleteButton={true}
        key={dataContext.currentAssessment+groupFilter}
        get={()=>get(dataContext.currentAssessment)}
        filterModel={{
          items: groupFilter ==='' ? [
          ] : [
            { id: 1, field: 'category', operator: 'contains', value: groupFilter } ,
          ],
        }}
        update={(row)=>{
          var item = row as Task;
          item.start = new Date(item.start);
          item.end = new Date(item.end);
          item.type = "task";
          item.styles  = {};
          item.styles.progressColor = row.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.progressSelectedColor = row.implementation == 1 ? '#e58909' : '#2b589b'
          item.styles.backgroundColor = row.implementation == 1 ? '#FE9D0C' : '#3367b9'
          item.styles.backgroundSelectedColor = row.implementation == 1 ? '#FE9D0C' : '#3367b9'
          setTasks(tasks.map(t => (t.id === item.id ? item : t)));
          return update(row, dataContext.currentAssessment)
        }}
        delete={destroy}
        reloadParentOnEditStop={true}
        name='organizationGrid'
        labels={{
          "actions": t('Actions'),
          "add": {
            "button": t('Add treatment')
          },
          "details": {
            "column":t('Controls'), 
            "button":t('View')
          }
        }} />}


        </AppLayout>
      </>
    )

}

export default Plan;